import { ReactNode, forwardRef } from 'react';

import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledSettingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadiusXl,
    border: `1px solid ${theme.palette.grey[100]}`,
    backgroundColor: theme?.palette.common.white,
}));

type SubtitleProp =
    | string
    | {
          content: string;
          typographyProps?: TypographyProps;
      };

interface SettingContainerProps {
    title: string;
    subtitle?: SubtitleProp;
    // The control for this setting, rendered on the right. Pass a `Switch` where the
    // current user can change the value, or an `OnOffChip` where they can't — a disabled
    // Switch is a poor third option, since it reads as "temporarily unavailable".
    control: ReactNode;
    // An extra line under the subtitle, e.g. why a read-only setting can't be changed here.
    note?: ReactNode;
}

/**
 * A labelled row for a single setting: title, optional subtitle, and a caller-supplied
 * control on the right.
 *
 * The control and note are passed in rather than configured through props, so this
 * component stays a layout concern and doesn't need to know about their APIs.
 */
const SettingContainer = forwardRef<HTMLDivElement, SettingContainerProps>(
    function SettingContainer({ title, subtitle, control, note }, ref) {
        const subtitleText = typeof subtitle === 'object' ? subtitle.content : subtitle;
        const subtitleProps = typeof subtitle === 'object' ? subtitle.typographyProps : {};

        return (
            <StyledSettingContainer ref={ref}>
                <Stack sx={{ gap: 0.5, flexGrow: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            {...subtitleProps}
                            variant="body2"
                            sx={{ color: 'grey.900', ...(subtitleProps?.sx || {}) }}
                        >
                            {subtitleText}
                        </Typography>
                    )}

                    {note}
                </Stack>

                {control}
            </StyledSettingContainer>
        );
    }
);

export default SettingContainer;
